import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { useCarga } from '../comun/useCarga';

/**
 * Lo que el backend `proyectos` (packages/backend/src/plugins/proyectos.ts)
 * lee de GitHub para cada proyecto. Los tipos repiten los del backend.
 */

export type Miembro = {
  login: string;
  avatar: string;
  url: string;
  permiso: string | null;
  contribuciones: number;
};

export type Ejecucion = {
  flujo: string;
  estado: string;
  conclusion: string | null;
  fecha: string;
  rama: string;
  url: string;
};

export type Cambio = {
  titulo: string;
  fecha: string;
  url: string;
  autor: string;
};

export type Repositorio = {
  slug: string;
  url: string;
  descripcion: string | null;
  wiki: string | null;
  ramaPrincipal: string;
  miembros: Miembro[];
  miembrosSegun: 'colaboradores' | 'contribuidores';
  pruebas: { configuradas: boolean; ejecuciones: Ejecucion[] };
  cambios: Cambio[];
  error?: string;
};

export type Entrega = {
  id: string;
  fase: string;
  texto: string;
  estado: 'completada' | 'pendiente' | 'retirada';
  detalle: string;
};

export type Avance = {
  fuente: string;
  fases: { titulo: string; estado: string; fecha: string | null }[];
  entregas: Entrega[];
};

const useBackend = <T>(ruta: string) => {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return useCarga<T>(async () => {
    const base = await discovery.getBaseUrl('proyectos');
    const respuesta = await fetchApi.fetch(`${base}${ruta}`);
    if (!respuesta.ok) {
      throw new Error(
        `El portal respondió ${respuesta.status} ${respuesta.statusText}`,
      );
    }
    return (await respuesta.json()) as T;
  }, [discovery, fetchApi, ruta]);
};

export const useRepositorios = (sistema: string) =>
  useBackend<{ repositorios: Repositorio[] }>(
    `/${encodeURIComponent(sistema)}/repositorios`,
  );

export const useAvance = (sistema: string) =>
  useBackend<{ avance: Avance | null; error?: string; fuente?: string }>(
    `/${encodeURIComponent(sistema)}/avance`,
  );

/** La última ejecución terminada de cada flujo de pruebas. */
export const ultimasPorFlujo = (ejecuciones: Ejecucion[]) => {
  const vistos = new Map<string, Ejecucion>();
  for (const e of ejecuciones) if (!vistos.has(e.flujo)) vistos.set(e.flujo, e);
  return [...vistos.values()];
};

/** Fecha corta en el idioma del navegador. */
export const fechaCorta = (iso: string) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

export type Grupo = { nombre: string; miembros: string[] };

export type GruposProyecto = {
  grupos: Grupo[];
  actualizadoPor: string | null;
  actualizadoEn: string | null;
  puedeEditar: boolean;
};

export type Candidato = { login: string; avatar: string; url: string };

/** Grupos del proyecto; cambiar `version` vuelve a pedirlos. */
export const useGrupos = (sistema: string, version: number) =>
  useBackend<GruposProyecto>(
    `/${encodeURIComponent(sistema)}/grupos?v=${version}`,
  );

export const useCandidatos = (sistema: string) =>
  useBackend<{ candidatos: Candidato[] }>(
    `/${encodeURIComponent(sistema)}/candidatos`,
  );

/** Guarda los grupos; devuelve el mensaje de error del portal si falla. */
export const useGuardarGrupos = () => {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return async (sistema: string, grupos: Grupo[]) => {
    const base = await discovery.getBaseUrl('proyectos');
    const respuesta = await fetchApi.fetch(
      `${base}/${encodeURIComponent(sistema)}/grupos`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grupos }),
      },
    );
    if (!respuesta.ok) {
      let mensaje = `${respuesta.status} ${respuesta.statusText}`;
      try {
        mensaje = (await respuesta.json())?.error?.message ?? mensaje;
      } catch {
        // Sin cuerpo JSON: queda el código HTTP.
      }
      throw new Error(mensaje);
    }
  };
};

/** Una historia del catálogo visual, tal como la lista el meta.json de Ladle. */
export type Historia = {
  id: string;
  nombre: string;
  /** Grupo y componente: ['Primitivos', 'Alert']. */
  niveles: string[];
  archivo: string;
  lineaInicio: number;
  lineaFin: number;
};

/**
 * Catálogo visual del proyecto: la base del sitio de Ladle que sirve el
 * backend y la lista de historias de su meta.json.
 */
export const useCatalogoVisual = (sistema: string) => {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return useCarga(async () => {
    const base = `${await discovery.getBaseUrl(
      'proyectos',
    )}/ladle/${encodeURIComponent(sistema)}/`;
    const respuesta = await fetchApi.fetch(`${base}meta.json`);
    if (!respuesta.ok) {
      throw new Error(
        `El catálogo visual respondió ${respuesta.status} ${respuesta.statusText}`,
      );
    }
    const meta = (await respuesta.json()) as {
      stories: Record<
        string,
        {
          name: string;
          levels: string[];
          filePath: string;
          locStart: number;
          locEnd: number;
        }
      >;
    };
    const historias: Historia[] = Object.entries(meta.stories).map(
      ([id, h]) => ({
        id,
        nombre: h.name,
        niveles: h.levels,
        archivo: h.filePath,
        lineaInicio: h.locStart,
        lineaFin: h.locEnd,
      }),
    );
    return { base, historias };
  }, [discovery, fetchApi, sistema]);
};
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { useCarga } from '../comun/useCarga';

/**
 * Lo que el backend `proyectos` (packages/backend/src/plugins/proyectos.ts)
 * lee de GitHub para cada proyecto. Los tipos repiten los del backend.
 */

export type Miembro = {
  login: string;
  avatar: string;
  url: string;
  permiso: string | null;
  contribuciones: number;
};

export type Ejecucion = {
  flujo: string;
  estado: string;
  conclusion: string | null;
  fecha: string;
  rama: string;
  url: string;
};

export type Cambio = {
  titulo: string;
  fecha: string;
  url: string;
  autor: string;
};

export type Repositorio = {
  slug: string;
  url: string;
  descripcion: string | null;
  wiki: string | null;
  ramaPrincipal: string;
  miembros: Miembro[];
  miembrosSegun: 'colaboradores' | 'contribuidores';
  pruebas: { configuradas: boolean; ejecuciones: Ejecucion[] };
  cambios: Cambio[];
  error?: string;
};

export type Entrega = {
  id: string;
  fase: string;
  texto: string;
  estado: 'completada' | 'pendiente' | 'retirada';
  detalle: string;
};

export type Avance = {
  fuente: string;
  fases: { titulo: string; estado: string; fecha: string | null }[];
  entregas: Entrega[];
};

const useBackend = <T>(ruta: string) => {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return useCarga<T>(async () => {
    const base = await discovery.getBaseUrl('proyectos');
    const respuesta = await fetchApi.fetch(`${base}${ruta}`);
    if (!respuesta.ok) {
      throw new Error(
        `El portal respondió ${respuesta.status} ${respuesta.statusText}`,
      );
    }
    return (await respuesta.json()) as T;
  }, [discovery, fetchApi, ruta]);
};

export const useRepositorios = (sistema: string) =>
  useBackend<{ repositorios: Repositorio[] }>(
    `/${encodeURIComponent(sistema)}/repositorios`,
  );

export const useAvance = (sistema: string) =>
  useBackend<{ avance: Avance | null; error?: string; fuente?: string }>(
    `/${encodeURIComponent(sistema)}/avance`,
  );

/** La última ejecución terminada de cada flujo de pruebas. */
export const ultimasPorFlujo = (ejecuciones: Ejecucion[]) => {
  const vistos = new Map<string, Ejecucion>();
  for (const e of ejecuciones) if (!vistos.has(e.flujo)) vistos.set(e.flujo, e);
  return [...vistos.values()];
};

/** Fecha corta en el idioma del navegador. */
export const fechaCorta = (iso: string) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

export type Grupo = { nombre: string; miembros: string[] };

export type GruposProyecto = {
  grupos: Grupo[];
  actualizadoPor: string | null;
  actualizadoEn: string | null;
  puedeEditar: boolean;
};

export type Candidato = { login: string; avatar: string; url: string };

/** Grupos del proyecto; cambiar `version` vuelve a pedirlos. */
export const useGrupos = (sistema: string, version: number) =>
  useBackend<GruposProyecto>(
    `/${encodeURIComponent(sistema)}/grupos?v=${version}`,
  );

export const useCandidatos = (sistema: string) =>
  useBackend<{ candidatos: Candidato[] }>(
    `/${encodeURIComponent(sistema)}/candidatos`,
  );

/** Guarda los grupos; devuelve el mensaje de error del portal si falla. */
export const useGuardarGrupos = () => {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return async (sistema: string, grupos: Grupo[]) => {
    const base = await discovery.getBaseUrl('proyectos');
    const respuesta = await fetchApi.fetch(
      `${base}/${encodeURIComponent(sistema)}/grupos`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grupos }),
      },
    );
    if (!respuesta.ok) {
      let mensaje = `${respuesta.status} ${respuesta.statusText}`;
      try {
        mensaje = (await respuesta.json())?.error?.message ?? mensaje;
      } catch {
        // Sin cuerpo JSON: queda el código HTTP.
      }
      throw new Error(mensaje);
    }
  };
};

/** Una historia del catálogo visual, tal como la lista el meta.json de Ladle. */
export type Historia = {
  id: string;
  nombre: string;
  /** Grupo y componente: ['Primitivos', 'Alert']. */
  niveles: string[];
  archivo: string;
  lineaInicio: number;
  lineaFin: number;
};

/**
 * Catálogo visual del proyecto: la base del sitio de Ladle que sirve el
 * backend y la lista de historias de su meta.json.
 */
export const useCatalogoVisual = (sistema: string) => {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return useCarga(async () => {
    const base = `${await discovery.getBaseUrl(
      'proyectos',
    )}/ladle/${encodeURIComponent(sistema)}/`;
    const respuesta = await fetchApi.fetch(`${base}meta.json`);
    if (!respuesta.ok) {
      throw new Error(
        `El catálogo visual respondió ${respuesta.status} ${respuesta.statusText}`,
      );
    }
    const meta = (await respuesta.json()) as {
      stories: Record<
        string,
        {
          name: string;
          levels: string[];
          filePath: string;
          locStart: number;
          locEnd: number;
        }
      >;
    };
    const historias: Historia[] = Object.entries(meta.stories).map(
      ([id, h]) => ({
        id,
        nombre: h.name,
        niveles: h.levels,
        archivo: h.filePath,
        lineaInicio: h.locStart,
        lineaFin: h.locEnd,
      }),
    );
    return { base, historias };
  }, [discovery, fetchApi, sistema]);
};
